import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TrackingUtils, withPageContext, Config } from '../../../utils';
import { CarouselBulletList } from './CarouselBulletList';
import { CarouselSlide } from './CarouselSlide';
import {
    SlideContainer,
    GradientContainer,
    SocialProofPortal,
    getBackgroundComponent,
} from './Carousel.style';
import { FadeTransition } from './FadeTransition';
import { SLIDE_TRANSITION_TIME } from './utils/constants';

const Carousel = ({ slides = [], children, onButtonClick, trackEvent }) => {
    const [counter, setCounter] = useState(0);
    const [mounted, setMounted] = useState(false);
    const [sliding, setSliding] = useState(slides.length > 1);
    const isRTL = Config.get('isRtl');

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        setSliding(slides.length > 1);
        setCounter((current) => (current < slides.length ? current : 0));
    }, [slides.length]);

    useEffect(() => {
        let timer;
        if (sliding && slides.length > 1) {
            timer = setInterval(() => {
                const counterValue = (counter + 1) % slides.length;
                setSlide(counterValue);
            }, SLIDE_TRANSITION_TIME);
        }

        return () => clearInterval(timer);
    }, [slides, counter, sliding]);

    const setSlide = (counterValue = 0) => {
        setCounter(counterValue);
    };

    const trackPromoClickEvent = () => {
        const slide = slides[counter];
        if (!slide || !slide.baseOffer) {
            return;
        }
        const { name } = slide.baseOffer;
        trackEvent({
            action: TrackingUtils.ACTION.CLICK,
            category: TrackingUtils.CATEGORY.INTERNAL_OFFER,
            label: TrackingUtils.LABEL.offerClick(name),
        });
    };

    const tackButtonClickEvent = (index) => {
        trackEvent({
            action: TrackingUtils.ACTION.CAROUSEL,
            label: TrackingUtils.LABEL.offerSelect(index),
        });
    };

    const onPromoClick = (e) => {
        e.preventDefault();
        trackPromoClickEvent();
    };

    const onPromoButtonClick = ({ href, target, index }) => {
        trackPromoClickEvent();
        tackButtonClickEvent(index);
        onButtonClick(href, target);
    };

    const getBackgroundSlide = (slide, index) => {
        const backgroundImage = slide.content.image.uri;
        const Background = getBackgroundComponent(backgroundImage);
        return (
            <FadeTransition
                key={index}
                in={mounted && counter === index}
                className="od-carousel-background"
            >
                {backgroundImage && (
                    <Background className="odf-position-absolute" onClick={onPromoClick} />
                )}
            </FadeTransition>
        );
    };

    const getSlide = (slide, index) => (
        <FadeTransition key={index} in={mounted && counter === index}>
            <div className="odf-position-absolute od-slide-container" onClick={onPromoClick}>
                <CarouselSlide
                    slide={slide}
                    onButtonClick={onPromoButtonClick}
                    trackEvent={trackEvent}
                    index={index}
                />
            </div>
        </FadeTransition>
    );

    return (
        <div className="carousel">
            {children && (
                <div className="odf-position-relative odf-space-outer-bottom-l od-carousel-children-container od-carousel-content-top">
                    {children}
                </div>
            )}

            {slides.map((slide, idx) => getBackgroundSlide(slide, idx))}
            <GradientContainer
                className="odf-position-absolute odf-bg-gradient-mono od-carousel-top"
                onClick={slides.length > 0 ? onPromoClick : undefined}
            />
            <SlideContainer className="odf-position-relative od-carousel-top">
                {slides.map((slide, idx) => getSlide(slide, idx))}
                {slides.length > 1 && (
                    <CarouselBulletList
                        className="od-carousel-top"
                        count={slides.length}
                        setSelected={setSlide}
                        selected={counter}
                        trackEvent={trackEvent}
                    />
                )}
                <SocialProofPortal
                    id="home_social_proof_desktop_container"
                    className="shell-integration_portal"
                    isRTL={isRTL}
                >
                    <div className="shell-integration_prerender od-home-social-proof-desktop-skeleton" />
                </SocialProofPortal>
            </SlideContainer>
        </div>
    );
};

Carousel.propTypes = {
    slides: PropTypes.array,
    children: PropTypes.node,
    onButtonClick: PropTypes.func,
    trackEvent: PropTypes.func.isRequired,
};

Carousel.defaultProps = {
    children: null,
    onButtonClick: () => {},
};

const PageContextCarousel = withPageContext(Carousel);

export { PageContextCarousel as Carousel };
